// Privacy-safe tracking for Customer Trust Centers only.
// - Never stores IP addresses, user agents, cookies, or fingerprints.
// - Respects Do-Not-Track (browser flag AND user-persisted opt-out).
// - visitor_id: anonymous uuid in localStorage. session_id: anon uuid in sessionStorage.
// - Fire-and-forget: uses sendBeacon when available, else fetch(keepalive) — never blocks UI.
// - Applies ONLY to /t/{slug} customer Trust Centers. Nova's own /trust does NOT use this.

const VISITOR_KEY = 'nova_tc_visitor_id';
const SESSION_KEY = 'nova_tc_session_id';
const OPT_OUT_KEY = 'nova_tc_opt_out';

const uid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '');
  } catch (_) {}
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

const safeLS = {
  get: (k) => { try { return window.localStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { window.localStorage.setItem(k, v); } catch (_) {} },
};

const safeSS = {
  get: (k) => { try { return window.sessionStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { window.sessionStorage.setItem(k, v); } catch (_) {} },
};

export const isDNT = () => {
  try {
    if (safeLS.get(OPT_OUT_KEY) === '1') return true;
    const nav = typeof navigator !== 'undefined' ? navigator : {};
    const dnt = nav.doNotTrack || nav.msDoNotTrack || (typeof window !== 'undefined' ? window.doNotTrack : null);
    return dnt === '1' || dnt === 'yes' || dnt === true;
  } catch (_) { return false; }
};

const getVisitorId = () => {
  let v = safeLS.get(VISITOR_KEY);
  if (!v) { v = uid(); safeLS.set(VISITOR_KEY, v); }
  return v;
};

const getSessionId = () => {
  let s = safeSS.get(SESSION_KEY);
  if (!s) { s = uid(); safeSS.set(SESSION_KEY, s); }
  return s;
};

const safeReferrer = () => {
  try {
    const ref = document.referrer || '';
    // Only pass a same-scheme http(s) URL; the backend will keep the host only.
    if (/^https?:\/\//i.test(ref)) return ref;
    return '';
  } catch (_) { return ''; }
};

const post = (url, body) => {
  try {
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
  } catch (_) {}
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (_) {}
};

export const trackEvent = (slug, eventType, extra = {}) => {
  if (!slug || !eventType) return;
  if (isDNT()) return;
  const body = {
    event_type: eventType,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    path: (typeof window !== 'undefined' && window.location) ? window.location.pathname : '',
    referrer: safeReferrer(),
    dnt: false,
    ...extra,
  };
  post(`/api/public/trust/${encodeURIComponent(slug)}/events`, body);
};

// Public helpers for the opt-out UI (future enhancement)
export const optOut = () => safeLS.set(OPT_OUT_KEY, '1');
export const optIn = () => safeLS.set(OPT_OUT_KEY, '0');
